import { GraphQlPriceType } from '@models/graphql';
import type { ProductAttribute, ProductAttributeValueAmountUnit } from './attribute.helper';
import { Length as EchainDomainLength, LengthUnits as EchainDomainLengthUnits } from '@igus/echain-shared-quantities';
import type { LengthJson } from '@igus/echain-shared-quantities';
import {
  CHAINFLEX_MAX_BUY_LENGTH,
  CHAINFLEX_MIN_BUY_LENGTH,
  E_CHAIN_MAX_BUY_LENGTH,
  E_CHAIN_MIN_BUY_LENGTH,
  LEAD_SCREW_MAX_BUY_LENGTH,
  LEAD_SCREW_MIN_BUY_LENGTH,
  LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_LENGTH,
  LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_LENGTH,
  LENGTH_WITH_CUTTING_COST_WITHOUT_COPPER_MAX_BUY_LENGTH,
  LENGTH_WITH_CUTTING_COST_WITHOUT_COPPER_MIN_BUY_LENGTH,
  TRIBOTAPE_MAX_BUY_LENGTH,
  TRIBOTAPE_MIN_BUY_LENGTH,
} from '@libs/constants';
import type { UnitCode } from '@icommerce/units';
import { LengthCode } from '@icommerce/units';

// Define a dictionary for unit conversions using EchainDomainLength as input
export const conversionFunction = (metric: EchainDomainLength, unitCode: UnitCode): number => {
  switch (String(unitCode)) {
    case LengthCode.METER:
      return metric.m;
    case LengthCode.MILLIMETER:
      return metric.mm;
    case LengthCode.INCH:
      return metric.inch;
    case LengthCode.FEET:
      return metric.ft;
    case LengthCode.KILOMETER:
      return metric.km;
    default:
      throw new Error(`Unsupported unit: ${unitCode}`);
  }
};

export interface MinMaxLengthConfig {
  minAttribute?: string;
  maxAttribute?: string;
  minLengthDefault: number;
  maxLengthDefault: number;
}

export interface MinMaxLengthConfigMap {
  [GraphQlPriceType.LengthAndPieceWithoutCopper]: MinMaxLengthConfig;
  [GraphQlPriceType.LengthWithCuttingCostWithoutCopper]: MinMaxLengthConfig;
  [GraphQlPriceType.ChainLink]: MinMaxLengthConfig;
  [GraphQlPriceType.LeadScrewLength]: MinMaxLengthConfig;
  [GraphQlPriceType.TribotapeLength]: MinMaxLengthConfig;
  default: MinMaxLengthConfig;
}
export const MIN_MAX_LENGTH_PER_PRICE_TYPE_CONFIG: MinMaxLengthConfigMap = {
  [GraphQlPriceType.LengthAndPieceWithoutCopper]: {
    minAttribute: 'min_gauge_X_metric',
    maxAttribute: 'max_length_metric',
    minLengthDefault: LENGTH_AND_PIECE_WITHOUT_COPPER_MIN_BUY_LENGTH,
    maxLengthDefault: LENGTH_AND_PIECE_WITHOUT_COPPER_MAX_BUY_LENGTH,
  },
  [GraphQlPriceType.LengthWithCuttingCostWithoutCopper]: {
    minAttribute: 'min_length_metric',
    maxAttribute: 'max_length_metric',
    minLengthDefault: LENGTH_WITH_CUTTING_COST_WITHOUT_COPPER_MIN_BUY_LENGTH,
    maxLengthDefault: LENGTH_WITH_CUTTING_COST_WITHOUT_COPPER_MAX_BUY_LENGTH,
  },
  [GraphQlPriceType.ChainLink]: {
    minAttribute: 'min_length_metric',
    maxAttribute: 'max_length_metric',
    minLengthDefault: E_CHAIN_MIN_BUY_LENGTH,
    maxLengthDefault: E_CHAIN_MAX_BUY_LENGTH,
  },
  [GraphQlPriceType.LeadScrewLength]: {
    minAttribute: 'min_length_metric',
    maxAttribute: 'max_length_metric',
    minLengthDefault: LEAD_SCREW_MIN_BUY_LENGTH,
    maxLengthDefault: LEAD_SCREW_MAX_BUY_LENGTH,
  },
  [GraphQlPriceType.TribotapeLength]: {
    minAttribute: 'min_length_metric',
    maxAttribute: 'max_length_metric',
    minLengthDefault: TRIBOTAPE_MIN_BUY_LENGTH,
    maxLengthDefault: TRIBOTAPE_MAX_BUY_LENGTH,
  },
  default: {
    minLengthDefault: CHAINFLEX_MIN_BUY_LENGTH,
    maxLengthDefault: CHAINFLEX_MAX_BUY_LENGTH,
  },
};

export const getEchainDomainLengthUnitByUnitCode = (unitCode: UnitCode): EchainDomainLengthUnits => {
  switch (unitCode) {
    case LengthCode.METER:
      return EchainDomainLengthUnits.m;
    case LengthCode.MILLIMETER:
      return EchainDomainLengthUnits.mm;
    case LengthCode.INCH:
      return EchainDomainLengthUnits.inch;
    case LengthCode.FEET:
      return EchainDomainLengthUnits.ft;
    case LengthCode.KILOMETER:
      return EchainDomainLengthUnits.km;
    default:
      throw new Error(`getEchainDomainLengthUnitByUnitCode - Unsupported unit: ${unitCode}`);
  }
};

/**
 * Converts a length value from one unit to another.
 *
 * @param attribute : ProductAttributeValueAmountUnit | LengthJson The attribute to convert .
 * @param toUnit: LengthCode - The target unit to convert to.
 * @param limitDecimalPlaces: boolean - flag to control truncation of result to 2 digits. Defaults to true
 * @returns The converted length value in the specified unit.
 * @throws Error if the target unit is not supported.
 */
export const convertProductAttributeValueAmountUnit = (
  attribute: ProductAttributeValueAmountUnit | ProductAttribute<ProductAttributeValueAmountUnit> | LengthJson,
  toUnit: UnitCode,
  limitDecimalPlaces = true
): number => {
  const value = (attribute as LengthJson)?.Value ?? (attribute as ProductAttributeValueAmountUnit)?.amount;
  const unit = (attribute as LengthJson)?.Unit ?? (attribute as ProductAttributeValueAmountUnit)?.unit;

  if (unit === toUnit || unit === LengthCode[toUnit] || !unit) {
    return value;
  }
  // casting the attribute value to EchainDomainLength
  let baseValue: EchainDomainLength;
  switch (unit) {
    case getEchainDomainLengthUnitByUnitCode(LengthCode.METER):
    case LengthCode.METER:
      baseValue = EchainDomainLength.m(value);
      break;
    case getEchainDomainLengthUnitByUnitCode(LengthCode.INCH):
    case LengthCode.INCH:
      baseValue = EchainDomainLength.inch(value);
      break;
    case getEchainDomainLengthUnitByUnitCode(LengthCode.FEET):
    case LengthCode.FEET:
      baseValue = EchainDomainLength.ft(value);
      break;
    case getEchainDomainLengthUnitByUnitCode(LengthCode.KILOMETER):
    case LengthCode.KILOMETER:
      baseValue = EchainDomainLength.km(value);
      break;
    case getEchainDomainLengthUnitByUnitCode(LengthCode.MILLIMETER):
    case LengthCode.MILLIMETER:
    default:
      baseValue = EchainDomainLength.mm(value);
  }

  return limitDecimalPlaces
    ? Math.floor(conversionFunction(baseValue, toUnit) * 100) / 100 // cut off the decimal places after 2 withouth rounding
    : conversionFunction(baseValue, toUnit);
};
